import React, { ReactNode, useState, useRef, useEffect, useLayoutEffect, useContext } from "react";
import cn from "classnames";
import { TreeFunnel, TreeFunnelNode } from "../tools/treeFunnel";
import SvgConnector from "../common/SvgConnector";
import ScaleControls from "../common/ScaleControls";
import useDragToScroll from "../hooks/useDragToScroll";
import { SettingsContext } from "../SettingsProvider";
import TrackingTooltip from "../common/TrackingTooltip";
import { formatWeight } from "../tools";

import styles from "./TreeCanvas.module.scss";

const NODE_WIDTH = 200;
const MIN_NODE_HEIGHT = 40;
const MIN_NODE_HEIGHT_NO_SCALE = 60; // make sure 3 rows of data fit on every node
const MIN_CANVAS_HEIGHT = 170;
const DEFAULT_CANVAS_HEIGHT = 500;
const MAX_SCALE = 1.5;
const MIN_SCALE = 0.1;

type DefsCache = { [id: string]: ReactNode };

interface Props {
    funnel: TreeFunnel;
    connectorYPadding: number;
    connectorYDefault: number;
    onSelectNode: (node: TreeFunnelNode | null) => void;
    selectedNode?: TreeFunnelNode;
}

const TreeCanvas: React.FC<Props> = function ({
    funnel,
    connectorYPadding,
    connectorYDefault,
    onSelectNode,
    selectedNode
}: Props) {
    const {
        settings: { unitTag, filterLevelsAbove, scaleBasedOnCount, numberColumn },
        labels
    } = useContext(SettingsContext);

    let visibleFunnel = funnel;
    if (filterLevelsAbove !== undefined) {
        visibleFunnel = funnel.slice(0, filterLevelsAbove);
    }
    const invisibleLevelsCount = funnel.length - visibleFunnel.length;
    const gridCount = visibleFunnel.length + Math.min(invisibleLevelsCount, 1);

    const minHeight = Math.min(
        ...visibleFunnel.map((level) => Math.min(...Object.values(level).map((node) => node.height)))
    );
    const effectiveMinNodeHeight = scaleBasedOnCount ? MIN_NODE_HEIGHT : MIN_NODE_HEIGHT_NO_SCALE;
    const canvasHeight = Math.max(effectiveMinNodeHeight / minHeight, MIN_CANVAS_HEIGHT);

    const headers = visibleFunnel.map((level, idx) => (
        <div key={idx} className={styles.header}>
            level {idx + 1}
        </div>
    ));

    const showWeights = numberColumn !== undefined;

    //
    // tooltips
    //
    const [tooltip, setTooltip] = useState<ReactNode>(null);

    const handleMouseEnterNode =
        (node: TreeFunnelNode) =>
        (event: React.MouseEvent): void => {
            let tooltipClass;
            if (frameRef && frameRef.current) {
                const frameRect = frameRef.current.getBoundingClientRect();
                if (event.clientX > frameRect.right - 200) {
                    tooltipClass = styles.tooltipLeft;
                }
            }

            const style = labels.labelsColors[node.label];

            const io = (data: { [label: string]: { count: number; weight: number } }): ReactNode[] => {
                return Object.keys(data).map((label) => {
                    const style = labels.labelsColors[label];
                    return (
                        <div key={label} className={styles.tooltipRow}>
                            <div className={styles.tooltipStatus} style={{ backgroundColor: style.color }}>
                                {labels.names[label]}
                            </div>
                            {showWeights && (
                                <div className={cn(styles.tooltipNumber, styles.textRight)}>
                                    {formatWeight(data[label].weight, unitTag)}
                                </div>
                            )}
                            <div className={cn(styles.tooltipNumber, styles.textRight)}>{data[label].count}</div>
                        </div>
                    );
                });
            };

            setTooltip(
                <TrackingTooltip className={tooltipClass}>
                    <div className={styles.tooltipContent}>
                        <div className={styles.tooltipRow}>
                            <div className={styles.tooltipStatus} style={{ backgroundColor: style.color }}>
                                {labels.names[node.label]}
                            </div>
                        </div>
                        {showWeights && (
                            <div className={styles.tooltipRow}>
                                <div className={styles.tooltipTitle}>Weight</div>
                                <div className={styles.tooltipNumber}>{formatWeight(node.weight, unitTag)}</div>
                            </div>
                        )}
                        <div className={styles.tooltipRow}>
                            <div className={styles.tooltipTitle}>Items</div>
                            <div className={styles.tooltipNumber}>{node.count}</div>
                        </div>
                        <div className={styles.separator} />
                        <div className={styles.tooltipRow}>
                            <div className={cn(styles.tooltipTitle, styles.strong)}>In ⇦</div>
                            {showWeights && <div className={cn(styles.tooltipTitle, styles.textCenter)}>Weight</div>}
                            <div className={cn(styles.tooltipTitle, styles.textCenter)}>Items</div>
                        </div>
                        {io(node.in)}
                        <div className={styles.separator} />
                        <div className={styles.tooltipRow}>
                            <div className={cn(styles.tooltipTitle, styles.strong)}>Out ⇨</div>
                        </div>
                        {io(node.out)}
                    </div>
                </TrackingTooltip>
            );
        };

    const handleMouseEnter =
        (node: TreeFunnelNode, outLabel: string) =>
        (event: React.MouseEvent): void => {
            let tooltipClass;
            if (frameRef && frameRef.current) {
                const frameRect = frameRef.current.getBoundingClientRect();
                if (event.clientX > frameRect.right - 200) {
                    tooltipClass = styles.tooltipLeft;
                }
            }

            const style = labels.labelsColors[node.label];
            const styleOut = labels.labelsColors[outLabel];

            setTooltip(
                <TrackingTooltip className={tooltipClass}>
                    <div className={styles.tooltipContent}>
                        <div className={styles.tooltipRow}>
                            <div className={styles.tooltipStatus} style={{ backgroundColor: style.color }}>
                                {labels.names[node.label]}
                            </div>
                            ⇨
                            <div className={styles.tooltipStatus} style={{ backgroundColor: styleOut.color }}>
                                {labels.names[outLabel]}
                            </div>
                        </div>
                        {showWeights && (
                            <div className={styles.tooltipRow}>
                                <div className={styles.tooltipTitle}>Weight</div>
                                <div className={styles.tooltipNumber}>
                                    {formatWeight(node.out[outLabel].weight, unitTag)}
                                </div>
                            </div>
                        )}
                        <div className={styles.tooltipRow}>
                            <div className={styles.tooltipTitle}>Items</div>
                            <div className={styles.tooltipNumber}>{node.out[outLabel].count}</div>
                        </div>
                    </div>
                </TrackingTooltip>
            );
        };

    const handleMouseLeave = (): void => {
        setTooltip(null);
    };

    //
    // nodes
    //

    const nodes = visibleFunnel.map((level, idx) => (
        <div key={idx} className={styles.nodeColumn}>
            {Object.keys(level).map((label) => {
                const node = level[label];
                const selected = node === selectedNode;
                const style = labels.labelsColors[node.label];
                return (
                    <div
                        key={`${idx}_${label}`}
                        className={cn(styles.node, {
                            [styles.selectedNode]: selected,
                            [styles.unselectedNode]: !selected && selectedNode !== undefined
                        })}
                        style={{
                            top: node.top * canvasHeight,
                            height: node.height * canvasHeight,
                            backgroundColor: selected || selectedNode === undefined ? style.color : style.colorInactive,
                            borderColor: style.border
                        }}
                        onClick={(event): void => {
                            event.stopPropagation();
                            onSelectNode(node);
                        }}
                        onMouseEnter={handleMouseEnterNode(node)}
                        onMouseLeave={handleMouseLeave}
                    >
                        <div className={styles.name}>{labels.names[label]}</div>
                        {showWeights && (
                            <div className={styles.count}>
                                <span className={"unicodeIcon"}>⚖</span> {formatWeight(node.weight, unitTag)}
                            </div>
                        )}
                        <div className={showWeights ? styles.percent : styles.count}>{node.count}</div>
                    </div>
                );
            })}
        </div>
    ));

    // insert dummy column when showing truncated number of levels
    if (gridCount > visibleFunnel.length) {
        nodes.push(<div key={gridCount} className={cn(styles.nodeColumn, styles.dummy)}></div>);
    }

    // generate all the gradients
    const defsCache: DefsCache = {};
    visibleFunnel.forEach((level) => {
        Object.values(level).forEach((node) => {
            const styleFrom = labels.labelsColors[node.label];
            Object.keys(node.out).forEach((outLabel) => {
                const styleTo = labels.labelsColors[outLabel];
                const fillKey = `lfill_${styleFrom.var_name}_${styleTo.var_name}`;
                if (!defsCache[fillKey]) {
                    defsCache[fillKey] = (
                        <linearGradient key={fillKey} id={fillKey}>
                            <stop offset="0%" stopColor={styleFrom.colorInactive} />
                            <stop offset="55%" stopColor={styleTo.colorInactive} />
                        </linearGradient>
                    );
                }
            });
        });
    });

    const getFlowSvg = (): ReactNode => {
        const topPerNode: { [label: string]: number }[] = []; // current y offset for each node's left side (input) when drawing connectors
        funnel.forEach((level, idx) => {
            topPerNode.push({});
            Object.keys(level).forEach((label) => (topPerNode[idx][label] = 0));
        });

        return visibleFunnel.map((level, idx) => {
            const last = idx === visibleFunnel.length - 1;
            return Object.keys(level).map((label) => {
                const node = level[label];
                const styleFrom = labels.labelsColors[node.label];
                const fromX = idx * NODE_WIDTH + NODE_WIDTH * 0.5;
                let currentTop = 0;
                return Object.keys(node.out).map((outLabel) => {
                    const { height: outHeight, level: outLevelIdx } = node.out[outLabel];
                    const outNode = funnel[outLevelIdx][outLabel];
                    let { height: inHeight } = outNode.in[label];
                    const fromY = (node.top + currentTop) * canvasHeight;
                    const toX = outLevelIdx * NODE_WIDTH;
                    let toY;
                    if (last) {
                        // level idx + 1 hasn't been scaled as it's not visible - just draw
                        // straight line
                        toY = fromY;
                        inHeight = outHeight;
                    } else {
                        toY = (outNode.top + topPerNode[outLevelIdx][outLabel]) * canvasHeight;
                    }

                    currentTop += outHeight;
                    topPerNode[outLevelIdx][outLabel] += inHeight;

                    return (
                        <SvgConnector
                            key={`${idx}_${label}_${outLabel}`}
                            fromX={fromX}
                            fromY={fromY}
                            toX={toX}
                            toY={toY}
                            heightL={outHeight * canvasHeight}
                            heightR={inHeight * canvasHeight}
                            paddingY={connectorYPadding}
                            lineX={6}
                            styleFrom={styleFrom}
                            styleTo={labels.labelsColors[outLabel]}
                            fillOpacity={last ? 0.3 : 1}
                            strokeOpacity={last ? 0.3 : 1}
                            strokeWidth={last ? 0 : 1}
                            className={styles.connector}
                            onMouseEnter={handleMouseEnter(node, outLabel)}
                            onMouseLeave={handleMouseLeave}
                        />
                    );
                });
            });
        });
    };

    const getFlowSvg2 = (): ReactNode =>
        visibleFunnel.map((level, idx) => {
            const last = idx === visibleFunnel.length - 1;
            return Object.keys(level).map((label) => {
                const node = level[label];
                const styleFrom = labels.labelsColors[node.label];
                const fromX = idx * NODE_WIDTH + NODE_WIDTH * 0.5;

                return Object.keys(node.out).map((outLabel, outIdx) => {
                    const { level: outLevelIdx } = node.out[outLabel];
                    const outNode = funnel[outLevelIdx][outLabel];
                    const fromY = (node.top + 0.5 * node.height) * canvasHeight - connectorYDefault * 0.5;
                    const toX = outLevelIdx * NODE_WIDTH;
                    let toY;
                    if (last) {
                        // level idx + 1 hasn't been scaled as it's not visible - just draw
                        // straight line
                        toY = fromY;
                    } else {
                        toY = (outNode.top + 0.5 * outNode.height) * canvasHeight - connectorYDefault * 0.5;
                    }

                    return (
                        <SvgConnector
                            key={`${idx}_${label}_${outLabel}`}
                            fromX={fromX}
                            fromY={fromY}
                            toX={toX}
                            toY={toY}
                            heightL={connectorYDefault}
                            heightR={connectorYDefault}
                            paddingY={connectorYPadding}
                            lineX={0}
                            styleFrom={styleFrom}
                            styleTo={labels.labelsColors[outLabel]}
                            fillOpacity={last ? 0.3 : 1}
                            strokeOpacity={last ? 0.3 : 1}
                            strokeWidth={last ? 0 : 1}
                            className={styles.connector}
                            onMouseEnter={handleMouseEnter(node, outLabel)}
                            onMouseLeave={handleMouseLeave}
                        />
                    );
                });
            });
        });

    //
    // scaling
    //

    const [scale, setScale] = useState<number>(1.0);
    const frameRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [frameHeight, setFrameHeight] = useState<number>();

    const handleZoomIn = (): void => {
        setScale(Math.min(scale + 0.1, MAX_SCALE));
    };

    const handleZoomOut = (): void => {
        setScale(Math.max(scale - 0.1, MIN_SCALE));
    };

    useEffect(() => {
        // give time for containerRef to update itself and return correct bounding rect
        setTimeout(() => {
            if (containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect();
                setFrameHeight(Math.max(rect.height, MIN_CANVAS_HEIGHT) + 10); // +10 to account for scrollbar height
            }
        }, 1);
    }, [scale, canvasHeight]);

    const handleViewAll = (): void => {
        if (frameRef.current) {
            const frameRect = frameRef.current.getBoundingClientRect();
            const sX = frameRect.width / (visibleFunnel.length * NODE_WIDTH);
            const sY = DEFAULT_CANVAS_HEIGHT / canvasHeight;
            const s = Math.max(MIN_SCALE, Math.min(1.0, sX, sY));
            setScale(s);
        }
    };

    const handleResetScale = (): void => {
        setScale(1.0);
    };

    // prevent flicker. useEffect would be executed after render showing wrong scale, before correction
    useLayoutEffect(() => {
        handleViewAll();
    }, [funnel]); //eslint-disable-line

    //
    // drag to scroll
    //

    const { handleMouseDown, isScrolling } = useDragToScroll({ elem: frameRef.current, scrollX: true, scrollY: false });

    return (
        <div className={styles.canvas}>
            <div
                ref={frameRef}
                className={styles.scrollableFrame}
                style={{ height: frameHeight }}
                onClick={(): void => {
                    onSelectNode(null);
                }}
            >
                <div
                    ref={containerRef}
                    className={cn(styles.container, { [styles.grabbing]: isScrolling })}
                    style={{ transform: `scale(${scale})` }}
                    onMouseDown={handleMouseDown}
                >
                    <div className={styles.content}>
                        <div className={styles.headers}>{headers}</div>

                        <div className={styles.tree} style={{ height: canvasHeight }}>
                            <div className={styles.grid}>
                                {Array(gridCount)
                                    .fill(undefined)
                                    .map((_, idx) => (
                                        <div key={idx} className={styles.gridColumn} />
                                    ))}
                            </div>

                            <svg className={styles.svg} style={{ height: canvasHeight }}>
                                <defs>{Object.values(defsCache)}</defs>
                                {scaleBasedOnCount ? getFlowSvg() : getFlowSvg2()}
                            </svg>

                            <div className={styles.nodes}>{nodes}</div>
                        </div>
                    </div>
                </div>
            </div>

            <div className={styles.controls}>
                <ScaleControls
                    scale={scale}
                    minScale={MIN_SCALE}
                    maxScale={MAX_SCALE}
                    onZoomIn={handleZoomIn}
                    onZoomOut={handleZoomOut}
                    onViewAll={handleViewAll}
                    onReset={handleResetScale}
                />
            </div>

            {tooltip}
        </div>
    );
};

export default TreeCanvas;
